import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { AppStore } from "../../../stores/app-store.service";
import { Subscription } from "rxjs";
import { NotificationService } from "../../../services/notification.service";
import { ExternalUserControllerService } from "../../../services/external-user-controller.service";
import { InternalUserControllerService } from "../../../services/internal-user-controller.service";
import {
  UserControllerService,
  Screen
} from "../../../services/user-controller.service";
import { environment } from "../../../../environments/environment";
import { HttpClientWrapper } from "../../../services/http-client.wrapper";
import { WebTrackingInitService } from "../../../services/web-tracking-init.service";
import packageInfo from "../../../../../package.json";
import { NgClass } from "@angular/common";
import { MatFormField, MatLabel, MatError } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./login.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    TranslateModule
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  private ssoReg: RegExp =
    /mpsa|opel|citroen|dsautomobiles|eurorepar|pcmarus|psa|peugeot|shiftup|vauxhall|stellantis/;
  private emailReg: RegExp =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/i;

  private readonly logUrl: string = "/users/log-version";
  private readonly notifier: NotificationService;
  private subscription: Subscription = new Subscription();
  private helper = new JwtHelperService();
  private id: string;
  private jwt: string;

  public loginForm: FormGroup;
  public sso: boolean = true;

  constructor(
    private formBuilder: FormBuilder,
    private _internaUserService: InternalUserControllerService,
    private _externalUserService: ExternalUserControllerService,
    private _router: Router,
    private _route: ActivatedRoute,
    private store: AppStore,
    private httpClientVersionLog: HttpClientWrapper<Boolean>,
    private notifierService: NotificationService,
    private _userService: UserControllerService,
    private webTrackingInitService: WebTrackingInitService
  ) {
    this.notifier = notifierService;
    this.loginForm = this.formBuilder.group({
      email: ["", Validators.required],
      pwd: ["", Validators.required]
    });
  }

  ngOnInit(): void {
    if (!this.loginSAML()) {
      this._internaUserService.logout();
      this.store.clear();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private internalLogin(email, pwd): void {
    this.subscription.add(
      this._internaUserService.login(email, btoa(pwd)).subscribe(
        (result) => {
          if (!result) {
            this.loginError(result);
            return;
          }
          if (result["profilId"].includes("/")) {
            result["profilId"] = result["profilId"].split("/")[0];
          }
          localStorage.setItem("profilId", result["profilId"]);
          this.logAppVersion();
          // Initialize web tracking after successful login
          this.initializeWebTracking(result);
          this._router.navigate(["/kpi"]);
        },
        (err) => {
          this.loginError(err);
        }
      )
    );
  }

  private externalLogin(): void {
    try {
      this.subscription.add(
        this._externalUserService.login().subscribe((result) => {
          console.log(result);
          window.open(result.name, "_self");
        })
      );
    } catch (e) {
      console.log(e.text);
    }
  }

  public async onSubmit(): Promise<void> {
    let emailValue = this.loginForm.controls["email"].value;
    const pwdValue = this.loginForm.controls["pwd"].value;

    if (emailValue) {
      if (!this.emailReg.test(emailValue)) {
        this.loginForm.controls["email"].setValue(`${emailValue}@mail.mail`);
        emailValue = `${emailValue}@mail.mail`;
      }

      if (this.ssoReg.test(emailValue)) {
        this.sso = true;
        this.externalLogin();
      } else {
        if (this.sso) {
          this.sso = false;
        } else if (pwdValue) {
          this.internalLogin(emailValue, pwdValue);
        }
      }
    }
  }

  loginSAML(): boolean {
    this._route.queryParams.subscribe((params) => {
      this.id = params["id"];
      this.jwt = params["jwt"];
    });
    if (this.id && this.jwt) {
      this.subscription.add(
        this._internaUserService.login(this.id, this.jwt).subscribe(
          (result) => {
            if (!result) {
              this.loginError(result);
              return true;
            }
            if (result["profilId"].includes("/")) {
              result["profilId"] = result["profilId"].split("/")[0];
            }
            localStorage.setItem("profilId", result["profilId"]);
            this.logAppVersion();
            // Initialize web tracking after successful SAML login
            this.initializeWebTracking(result);
            this._router.navigate(["/kpi"]);
          },
          (err) => {
            this.loginError(err);
          }
        )
      );
    } else {
      return false;
    }
  }

  private loginError(err): void {
    const title: string = "Unable to login";
    let message: string;

    if (err && !err.error.error) {
      message = "Please try again later";
    } else {
      if (err.error.error.statusCode === 401) {
        message = err.error.error.message;
      } else {
        message = "Please try again later";
      }
    }
    this.notifier.error(`${title}: ${message}`);
  }

  private logAppVersion(): void {
    try {
      console.log(this.getUsernameOrPosition(), "front", packageInfo.version);
      this.httpClientVersionLog
        .postUnique(`${this.getEnvUrl()}${this.logUrl}`, {
          username: this.getUsernameOrPosition(),
          app: "front",
          version: packageInfo.version
        })
        .subscribe((hasLoggedVersion) => {
          if (hasLoggedVersion) {
            console.log("log version success");
          } else {
            console.log("log version failed");
          }
        });
    } catch (e) {
      console.log(e);
    }
  }

  private getUsernameOrPosition(): string {
    let username = "";
    if (window.localStorage.getItem("username") == null) {
      return "No username (force refresh working ?)";
    } else {
      username = window.localStorage.getItem("username");
    }
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    // Taille réelle (CSS pixels)
    const viewportWidth = window.innerWidth;
    const screen: Screen = {
      username: `username : ${username}`,
      resolution: `résolution ${screenWidth} x ${screenHeight}`,
      zoom: `zoom calculé : ${Math.round((screenWidth / viewportWidth) * 100)}`
    };

    this.subscription.add(this._userService.postScreen(screen).subscribe());

    return username;
  }

  private getEnvUrl(): string {
    return environment.apiBaseUrl;
  }

  private initializeWebTracking(loginResult: any): void {
    console.log("🔌 Starting web tracking initialization...", loginResult);

    try {
      const username =
        loginResult.username ||
        window.localStorage.getItem("username") ||
        "anonymous";

      console.log("👤 Initializing tracking for:", { username });

      this.webTrackingInitService.initialize(username, loginResult.profilId);

      console.log("✅ Web tracking initialization completed");
    } catch (error) {
      console.error("❌ Failed to initialize web tracking:", error);
    }
  }
}
